import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../styles/ThemeProvider';

/**
 * One value-picking chip (GET-266). Selection is shown by a 2px textPrimary border, never a
 * teal fill. The selected chip trims 1px of padding on each side so it keeps the same size.
 * Action buttons are not choice chips (Item Detail's Edit/Delete keep their own style).
 */
export interface ChoiceChipProps {
  label: string;
  selected: boolean;
  onPress: () => void;
  /** Renders a 16px colour dot before the label. */
  swatchColor?: string;
  /** Optional leading icon (e.g. the Cache "Needs Info" filter's alert). */
  icon?: keyof typeof Ionicons.glyphMap;
  /** Icon colour; defaults to textSecondary. */
  iconColor?: string;
  disabled?: boolean;
  testID?: string;
}

const PAD_V = 8;
const PAD_H = 12;
const SWATCH = 16;

export function ChoiceChip({
  label,
  selected,
  onPress,
  swatchColor,
  icon,
  iconColor,
  disabled,
  testID,
}: ChoiceChipProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        chip: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          minHeight: 44,
          backgroundColor: theme.colors.backgroundSurface,
          borderRadius: theme.radius.radiusMd,
          borderWidth: 1,
          borderColor: theme.colors.border,
          paddingVertical: PAD_V,
          paddingHorizontal: PAD_H,
        },
        chipSelected: {
          borderWidth: 2,
          borderColor: theme.colors.textPrimary,
          paddingVertical: PAD_V - 1,
          paddingHorizontal: PAD_H - 1,
        },
        swatch: {
          width: SWATCH,
          height: SWATCH,
          borderRadius: SWATCH / 2,
          borderWidth: 1,
          borderColor: theme.colors.border,
        },
        text: { ...theme.typography.sm, color: theme.colors.textPrimary },
      }),
    [theme]
  );

  return (
    <Pressable
      style={[styles.chip, selected && styles.chipSelected]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected, disabled: !!disabled }}
      // RN-web emits no aria-selected for role="button", so on web the selected state goes
      // into the label. Native platforms announce accessibilityState.selected themselves.
      accessibilityLabel={Platform.OS === 'web' && selected ? `${label}, selected` : undefined}
      testID={testID}
    >
      {swatchColor != null && (
        <View
          style={[styles.swatch, { backgroundColor: swatchColor }]}
          testID={testID ? `${testID}-swatch` : 'choice-chip-swatch'}
        />
      )}
      {icon != null && (
        <Ionicons name={icon} size={14} color={iconColor ?? theme.colors.textSecondary} />
      )}
      <Text style={styles.text}>{label}</Text>
    </Pressable>
  );
}

export default ChoiceChip;
